import { ArrowDownRight, ArrowUpRight, Check, HardHat, MapPin, ShieldCheck, Timer } from "lucide-react";
import { Link } from "react-router-dom";
import { useIndustries } from "@/hooks/useIndustries";

const heroImage = "https://images.unsplash.com/photo-1579847188804-ecba0e2ea330?auto=format&fit=crop&w=2200&q=85";
const civilImage = "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1400&q=85";
const constructionImage = "https://images.unsplash.com/photo-1531834685032-c34bf0d84c77?auto=format&fit=crop&w=1400&q=85";
const warehouseImage = "https://images.unsplash.com/photo-1586528116022-aeda1613c63d?auto=format&fit=crop&w=1000&q=85";
const miningImage = "https://images.unsplash.com/photo-1690897606066-0aa88b849203?auto=format&fit=crop&w=1000&q=85";

const industries = [
  ["Civil & Infrastructure", civilImage, "industry-civil-card"],
  ["Construction", constructionImage, "industry-construction-card"],
  ["Mining & Earthmoving", miningImage, "industry-mining-card"],
  ["Warehousing & Logistics", warehouseImage, "industry-warehouse-card"],
];

export default function HomePage() {
  const activeIndustries = useIndustries().slice(0, 4);
  return (
    <main>
      <section className="hero" style={{ backgroundImage: `linear-gradient(90deg, rgba(10,10,10,.9) 0%, rgba(10,10,10,.58) 55%, rgba(10,10,10,.2) 100%), url(${heroImage})` }} data-testid="home-hero">
        <div className="hero-content">
          <p className="eyebrow light-eyebrow" data-testid="hero-location">ADELAIDE LABOUR HIRE & RECRUITMENT</p>
          <h1 data-testid="hero-heading"><span>RIGHT</span> PEOPLE<br /><span>RIGHT</span> SKILLS<br /><span>RIGHT</span> NOW</h1>
          <p className="hero-copy" data-testid="hero-description">Dependable people. Ready when the work is. Right Hire delivers responsive, compliant workforce support for South Australia’s toughest industries.</p>
          <div className="hero-actions">
            <Link className="button-primary" to="/employers" data-testid="hero-find-staff-button">Find Staff <ArrowUpRight size={18} /></Link>
            <Link className="button-secondary light" to="/job-seekers" data-testid="hero-find-work-button">Find Work <ArrowDownRight size={18} /></Link>
          </div>
        </div>
        <div className="hero-corner-note" data-testid="hero-availability-note"><span className="availability-dot" /> Same-day labour available</div>
      </section>

      <section className="intro-section section-pad" data-testid="about-preview-section">
        <div className="section-label">01 / BUILT FOR THE WORK</div>
        <div className="intro-layout">
          <h2>WORKFORCE SUPPORT<br />THAT <span>SHOWS UP.</span></h2>
          <div>
            <p className="lead-copy">Right Hire Personnel connects dependable people with the businesses building South Australia.</p>
            <p>From a single reliable hand to an ongoing site crew, we make labour hire clear, fast and accountable. Our team knows the work, the tickets and the pace it takes to deliver.</p>
            <Link to="/about" className="text-link" data-testid="about-more-link">Meet Right Hire <ArrowUpRight size={16} /></Link>
          </div>
        </div>
      </section>

      <section className="industries-section" data-testid="industries-preview-section">
        <div className="section-head section-pad-horizontal">
          <div><p className="section-label">02 / WHERE WE WORK</p><h2>INDUSTRIES<br />WE SERVICE.</h2></div>
          <Link to="/industries" className="text-link" data-testid="all-industries-link">Explore all industries <ArrowUpRight size={16} /></Link>
        </div>
        <div className="industry-grid">
          {activeIndustries.map((industry, index) => (
            <Link to="/industries" className={`industry-card industry-card-${index}`} key={industry.slug} data-testid={`industry-${industry.slug}-card`}>
              <img src={industry.image_url} alt={industry.name} data-testid={`industry-${industry.slug}-card-image`} />
              <div className="industry-overlay"><p>{String(index + 1).padStart(2, "0")}</p><h3>{industry.name}</h3><ArrowUpRight /></div>
            </Link>
          ))}
        </div>
      </section>

      <section className="reasons-section section-pad" data-testid="why-choose-us-section">
        <div className="section-label">03 / WHY RIGHT HIRE</div>
        <div className="reasons-layout">
          <h2>NO GUESSWORK.<br /><span>JUST GOOD PEOPLE.</span></h2>
          <div className="reason-list">
            <article><Timer /><div><h3>Fast, responsive coverage</h3><p>Urgent fill-ins and planned workforce support without the run-around.</p></div></article>
            <article><ShieldCheck /><div><h3>Compliance-minded</h3><p>Tickets, licences and worker readiness checked before they hit site.</p></div></article>
            <article><HardHat /><div><h3>Industry-led recruitment</h3><p>People who understand your role, not just your job title.</p></div></article>
          </div>
        </div>
      </section>

      <section className="process-section section-pad" data-testid="process-section">
        <div className="section-label">04 / HOW IT WORKS</div>
        <div className="process-head"><h2>THE RIGHT FIT,<br />WITHOUT THE FRICTION.</h2><p>Clear briefs. Straight answers. Workers who are ready to move.</p></div>
        <div className="process-grid">
          {["Tell us what you need", "We find the right match", "Your workforce gets moving"].map((item, index) => <article key={item} data-testid={`process-step-${index + 1}`}><span>0{index + 1}</span><h3>{item}</h3><p className="process-brand-label" data-testid={`process-brand-label-${index + 1}`}><b>RIGHT</b> {["PEOPLE", "SKILLS", "NOW"][index]}</p><Check size={21} /></article>)}
        </div>
      </section>

      <section className="jobs-preview section-pad" data-testid="jobs-preview-section">
        <div><p className="section-label">05 / OPPORTUNITIES</p><h2>READY WHEN<br />THE NEXT ROLE IS.</h2></div>
        <div className="empty-mini"><MapPin size={22} /><p data-testid="jobs-preview-empty-message">New roles are added regularly. Register today and we’ll reach out when the right one lands.</p><Link className="button-primary" to="/job-seekers" data-testid="jobs-preview-register-button">Register with us <ArrowUpRight size={18} /></Link></div>
      </section>

      <section className="cta-band" data-testid="home-contact-cta"><div><p className="section-label light-eyebrow">LET’S GET TO WORK</p><h2>NEED PEOPLE<br />WHO CAN <span>DELIVER?</span></h2></div><Link className="button-primary" to="/contact" data-testid="home-contact-button">Speak with our team <ArrowUpRight size={18} /></Link></section>
    </main>
  );
}